import { useTranslation } from "react-i18next";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import Modal from "../../../../../parts/modal-new";
import { mealPhotoCommentActions } from "../../../../../parts/modals/thread-comments/comment-action-configs";
import { CommentsSection } from "../../../../../parts/modals/thread-comments/comments-section";
import SvgIcon from "../../../../../parts/SvgIcon";
import { EATING_TAGS } from "./constants";

const PhotosCarousel = ({
  photos = [],
  selectedPhotoIndex = 0,
  isOpen = false,
  onClose,
  onPhotoChange,
  date,
  locals,
  utcDate,
}) => {
  const { t } = useTranslation("athlete-portal");
  const [currentIndex, setCurrentIndex] = useState(selectedPhotoIndex);
  const currentPhoto = photos[currentIndex];
  // locals.athlete is only set when a coach is viewing an athlete's page
  // (ensure-coach middleware). When the athlete views their own page, fall
  // back to activeUser as the photo owner and their coach as the receiver.
  const athleteId = locals?.athlete?._id ?? locals?.activeUser?._id;
  const receiverId = locals?.athlete?._id ?? locals?.activeUser?.coach?._id;

  const scrollToThumbnail = useCallback((index) => {
    setTimeout(() => {
      const container = document.querySelector(
        ".photo-carousel-thumbnails-container"
      );
      const thumbnailElement = document.querySelector(
        `.photo-carousel-thumbnail:nth-child(${index + 1})`
      );

      if (container && thumbnailElement) {
        // Calculate the position to scroll to center the thumbnail
        const containerWidth = container.clientWidth;
        const thumbnailWidth = thumbnailElement?.offsetWidth;
        const thumbnailOffsetLeft = thumbnailElement?.offsetLeft;

        // Calculate scroll position to center the thumbnail
        const scrollLeft =
          thumbnailOffsetLeft - containerWidth / 2 + thumbnailWidth / 2;

        container.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    }, 100); // Slightly longer delay to ensure DOM is ready
  }, []);

  // Update current index when selectedPhotoIndex changes
  useEffect(() => {
    setCurrentIndex(selectedPhotoIndex);
    if (isOpen) {
      scrollToThumbnail(selectedPhotoIndex);
    }
  }, [selectedPhotoIndex, isOpen, scrollToThumbnail]);

  const goToNext = useCallback(() => {
    const nextIndex = (currentIndex + 1) % photos.length;
    setCurrentIndex(nextIndex);
    onPhotoChange?.(nextIndex);
    scrollToThumbnail(nextIndex);
  }, [currentIndex, photos.length, onPhotoChange, scrollToThumbnail]);

  const goToPrevious = useCallback(() => {
    const prevIndex = currentIndex === 0 ? photos.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    onPhotoChange?.(prevIndex);
    scrollToThumbnail(prevIndex);
  }, [currentIndex, photos.length, onPhotoChange, scrollToThumbnail]);

  // Handle keyboard navigation (skip when user is typing in an input/textarea)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      const isTyping =
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        e.target.isContentEditable;
      if (isTyping && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
        return;
      }

      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowLeft":
          e.preventDefault();
          goToPrevious();
          break;
        case "ArrowRight":
          e.preventDefault();
          goToNext();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, goToPrevious, goToNext, onClose]);

  const goToPhoto = (index) => {
    setCurrentIndex(index);
    onPhotoChange?.(index);
    scrollToThumbnail(index);
  };

  if (!isOpen || photos.length === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("mealPhotosPage.mealPhotosTitle", { date })}
      className="photo-carousel-modal"
      canClose={true}
      toClear={["threadId", "dailyStatId", "photoIndex", "photoId"]}
    >
      <div className="photo-carousel-content">
        {/* Main photo and info container */}
        <div className="photo-carousel-main">
          <main className="photo-carousel-image-container">
            <img
              src={currentPhoto.image.s3Path}
              alt={`${currentPhoto.mealType} photo taken at ${moment(
                currentPhoto.timestamp
              ).format("MMM D, YYYY h:mm A")}`}
              className="photo-carousel-image"
            />
          </main>

          {/* Photo info */}
          <aside>
            <div className="photo-carousel-info">
              <h3 className="photo-carousel-meal-type">
                {currentPhoto.mealType}
              </h3>
              <div className="photo-carousel-time-container">
                <img
                  src={"/img/new-icons/vuesax-linear-clock.svg"}
                  alt="Clock"
                  className="photo-carousel-clock-icon"
                />
                <p className="photo-carousel-time">
                  {moment(currentPhoto.timestamp).format("h:mmA")}
                </p>
              </div>

              {currentPhoto.mealTags && currentPhoto.mealTags.length > 0 && (
                <div className="photo-carousel-tags-section">
                  <h4 className="photo-carousel-tags-title">
                    Reasons for eating:
                  </h4>
                  <div className="photo-carousel-tags-list">
                    {currentPhoto.mealTags.map((tag, index) => (
                      <div key={index} className="photo-carousel-tag">
                        <span className="photo-carousel-tag-emoji">
                          {EATING_TAGS.find((t) => t.text === tag)?.emoji}{" "}
                        </span>
                        {tag.charAt(0).toUpperCase() + tag.slice(1)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {currentPhoto.notes && (
                <p className="photo-carousel-notes">{currentPhoto.notes}</p>
              )}
            </div>
            <div className="photo-carousel-thread-wrapper">
              <div className="photo-carousel-thread-wrapper-header">
                <span>Comments</span>
              </div>
              <CommentsSection
                commentActions={mealPhotoCommentActions}
                commentParams={{
                  mealPhotoId: currentPhoto?._id,
                  athleteId,
                  receiverId,
                  mealType: currentPhoto?.mealType,
                  date,
                  threadId: currentPhoto?.thread?._id,
                  dailyStatId: locals?.query?.dailyStatId,
                  utcDate,
                }}
                locals={locals}
                photoId={currentPhoto?._id}
                isMealPhotos
              />
            </div>
          </aside>
        </div>

        {/* Thumbnail previews — desktop only */}
        {photos.length > 1 && (
          <div className="photo-carousel-thumbnails mobile-hidden">
            <button
              className="photo-carousel-nav-btn photo-carousel-prev"
              onClick={goToPrevious}
              aria-label={t("mealPhotosPage.previousPhoto")}
            >
              <SvgIcon
                noFilterOnLight={true}
                src="/img/new-icons/arrow-left.svg"
              />
            </button>

            <div className="photo-carousel-thumbnails-container">
              {photos.map((photo, index) => (
                <button
                  key={photo._id}
                  className={`photo-carousel-thumbnail ${
                    index === currentIndex ? "active" : ""
                  }`}
                  onClick={() => goToPhoto(index)}
                  aria-label={t("mealPhotosPage.goToPhoto", {
                    index: index + 1,
                  })}
                >
                  <img
                    src={photo.image.s3Path}
                    alt={`${photo.mealType} thumbnail`}
                    className="photo-carousel-thumbnail-image"
                  />
                </button>
              ))}
            </div>

            <button
              className="photo-carousel-nav-btn photo-carousel-next"
              onClick={goToNext}
              aria-label={t("mealPhotosPage.nextPhoto")}
            >
              <SvgIcon
                noFilterOnLight={true}
                src="/img/new-icons/arrow-right.svg"
              />
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PhotosCarousel;
